import removeFavoriteUseCase from 'lib/UseCases/removeFavorite';
import addFavoriteUseCase from 'lib/UseCases/addFavorite';
import { createAction, createReducer, Dispatch } from '@reduxjs/toolkit';
import * as _ from 'lodash-es';
import { FavoriteResponseItem, FavoriteDataItem } from 'types/Apis/Favorites';
import State, { FavoritesSlice, RequestStates, AlertTypes } from 'types/State';
import fetchFavoritesUseCase from 'lib/UseCases/fetchFavorites';
import i18n from 'lib/i18next';
import captureException from 'lib/captureException';
import { signedOut } from './session';
import { displayAlert } from './alerts';

const favoritesReceived = createAction<FavoriteResponseItem[]>('favorites/received');
const favoritesRequested = createAction('favorites/requested');
const favoritesFailed = createAction<string>('favorites/error');
const favoriteRemoved = createAction<string>('favorites/removed');
const favoriteAdded = createAction<FavoriteResponseItem>('favorites/added');
const favoriteAddedByVinOnly = createAction<{ vin: string }>('favorites/addedByVinOnly');

const favoritesReducer = createReducer<FavoritesSlice>(
  {
    requestState: RequestStates.Unstarted,
    byVin: {},
    allVins: [],
  },
  {
    [favoriteAddedByVinOnly.type]: (state, action) => {
      return {
        ...state,
        allVins: [...state.allVins, action.payload.vin],
      };
    },
    [favoriteRemoved.type]: (state, action) => {
      return {
        ...state,
        byVin: _.omit(state.byVin, action.payload),
        allVins: state.allVins.filter((r) => r !== action.payload),
      };
    },
    [favoriteAdded.type]: (state, action) => {
      return {
        ...state,
        byVin: { ...state.byVin, [action.payload.vin]: action.payload },
        allVins: [...state.allVins, action.payload.vin],
      };
    },
    [favoritesFailed.type]: (state, action) => {
      return {
        byVin: {},
        allVins: [],
        requestState: RequestStates.Failed,
        error: action.payload,
      };
    },
    [favoritesRequested.type]: (state) => {
      return {
        ...state,
        requestState: RequestStates.Requested,
      };
    },
    [favoritesReceived.type]: (state, action) => {
      if (state.requestState !== RequestStates.Requested) {
        return state;
      }
      const byVin: FavoritesSlice['byVin'] = {};

      const allVins = action.payload
        .filter((f: FavoriteResponseItem) => f.search_vehicle)
        .map((favorite: FavoriteDataItem) => {
          byVin[favorite.vin] = favorite;
          return favorite.vin;
        });

      return {
        byVin,
        allVins,
        requestState: RequestStates.Finished,
      };
    },
    [signedOut.type]: () => {
      return {
        byVin: {},
        allVins: [],
        requestState: RequestStates.Unstarted,
      };
    },
  }
);

export function removeFavorite(vin: string) {
  return async function favoriteRemover(
    dispatch: Dispatch<any>,
    getState: () => State
  ): Promise<void> {
    try {
      const id = getState()?.favorites?.byVin[vin]?.id;
      await removeFavoriteUseCase(id);
      dispatch(favoriteRemoved(vin));
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: i18n.t('auth.errors.generic'),
          alertTime: Infinity,
          debug: _.toPlainObject(e),
        })
      );
    }
  };
}

export function addFavorite(vin: string) {
  return async function favoriteAdder(dispatch: Dispatch<any>): Promise<void> {
    try {
      const response = await addFavoriteUseCase(vin);
      dispatch(favoriteAdded(response));
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: i18n.t('auth.errors.generic'),
          alertTime: Infinity,
          debug: _.toPlainObject(e),
        })
      );
      captureException(e);
    }
  };
}

export function fetchFavorites() {
  return async function favoriteFetcher(
    dispatch: Dispatch<any>,
    getState: () => State
  ): Promise<void> {
    dispatch(favoritesRequested());
    try {
      const response = await fetchFavoritesUseCase();
      const { favorites } = response;
      const isSignedIn = getState()?.session?.isSignedIn;
      if (!isSignedIn) {
        return;
      }

      dispatch(favoritesReceived(favorites));
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: i18n.t('auth.errors.generic'),
          alertTime: Infinity,
          debug: e.toString(),
        })
      );
      dispatch(favoritesFailed(e.message));
    }
  };
}

export {
  favoritesReceived,
  favoritesReducer,
  favoriteRemoved,
  favoriteAdded,
  favoriteAddedByVinOnly,
};
